// Pure, browser-local subtitle converters shared by the SRT ⇄ VTT tools.
// No DOM, no network: plain string transforms so they are safe to run during
// render and in either direction.

const TIMING_LINE = /-->/;

// Matches a single SRT/VTT timestamp: optional hours, then MM:SS with a
// comma or period millisecond separator (1–3 digits).
const TIMESTAMP = /(?:(\d{1,2}):)?(\d{1,2}):(\d{1,2})[.,](\d{1,3})/;

function normalizeNewlines(input: string): string {
  return input.replace(/\r\n?/g, '\n').replace(/^﻿/, '');
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function splitBlocks(text: string): string[] {
  return text
    .trim()
    .split(/\n{2,}/)
    .map((block) => block.replace(/^\n+|\n+$/g, ''))
    .filter((block) => block.length > 0);
}

// Parse one timestamp string into its parts, tolerating a missing hours field.
function parseTimestamp(raw: string): { h: number; m: number; s: number; ms: number } | null {
  const match = raw.match(TIMESTAMP);
  if (!match) {
    return null;
  }
  const h = match[1] ? Number(match[1]) : 0;
  const m = Number(match[2]);
  const s = Number(match[3]);
  const ms = Number((match[4] + '000').slice(0, 3));
  return { h, m, s, ms };
}

function toVttTimestamp(raw: string): string | null {
  const parts = parseTimestamp(raw);
  if (!parts) {
    return null;
  }
  return `${pad2(parts.h)}:${pad2(parts.m)}:${pad2(parts.s)}.${String(parts.ms).padStart(3, '0')}`;
}

function toSrtTimestamp(raw: string): string | null {
  const parts = parseTimestamp(raw);
  if (!parts) {
    return null;
  }
  return `${pad2(parts.h)}:${pad2(parts.m)}:${pad2(parts.s)},${String(parts.ms).padStart(3, '0')}`;
}

// Extract the start/end timestamps from a cue timing line, dropping any WebVTT
// cue settings (e.g. `align:start position:50%`) that trail the end time.
function parseTimingLine(line: string): { start: string; end: string } | null {
  const halves = line.split('-->');
  if (halves.length < 2) {
    return null;
  }
  const startRaw = halves[0].trim();
  const endRaw = halves[1].trim();
  const start = parseTimestamp(startRaw);
  const end = parseTimestamp(endRaw);
  if (!start || !end) {
    return null;
  }
  return { start: startRaw, end: endRaw };
}

export type SrtToVttOptions = {
  keepCueIds?: boolean;
};

// Convert SubRip (.srt) text into WebVTT (.vtt): add the WEBVTT header, switch
// comma millisecond separators to periods, and either drop numeric cue markers
// or keep them as VTT cue identifiers.
export function convertSrtToVtt(input: string, options: SrtToVttOptions = {}): string {
  const keepCueIds = options.keepCueIds ?? false;
  const normalized = normalizeNewlines(input);
  const blocks = splitBlocks(normalized);
  const out: string[] = ['WEBVTT', ''];

  for (const block of blocks) {
    const lines = block.split('\n');
    let index = 0;
    let cueId: string | null = null;

    if (/^\d+$/.test(lines[0].trim()) && lines[1] && TIMING_LINE.test(lines[1])) {
      cueId = lines[0].trim();
      index = 1;
    }

    const timingLine = lines[index];
    if (!timingLine || !TIMING_LINE.test(timingLine)) {
      continue;
    }

    const timing = parseTimingLine(timingLine);
    if (!timing) {
      continue;
    }

    const start = toVttTimestamp(timing.start);
    const end = toVttTimestamp(timing.end);
    if (!start || !end) {
      continue;
    }

    const cueLines: string[] = [];
    if (keepCueIds && cueId) {
      cueLines.push(cueId);
    }
    cueLines.push(`${start} --> ${end}`);
    for (let i = index + 1; i < lines.length; i += 1) {
      cueLines.push(lines[i]);
    }

    out.push(cueLines.join('\n'));
    out.push('');
  }

  return `${out.join('\n').trimEnd()}\n`;
}

// Convert WebVTT (.vtt) text into SubRip (.srt): strip the WEBVTT header plus
// NOTE/STYLE/REGION blocks, drop cue settings, renumber cues from 1, and switch
// period millisecond separators to commas.
export function convertVttToSrt(input: string): string {
  const normalized = normalizeNewlines(input);
  const blocks = splitBlocks(normalized);
  const out: string[] = [];
  let cueNumber = 0;

  for (const block of blocks) {
    const lines = block.split('\n');
    const firstWord = lines[0].trim().split(/\s+/)[0];

    if (
      firstWord === 'WEBVTT' ||
      firstWord === 'NOTE' ||
      firstWord === 'STYLE' ||
      firstWord === 'REGION'
    ) {
      continue;
    }

    const timingIndex = lines.findIndex((line) => TIMING_LINE.test(line));
    if (timingIndex === -1) {
      continue;
    }

    const timing = parseTimingLine(lines[timingIndex]);
    if (!timing) {
      continue;
    }

    const start = toSrtTimestamp(timing.start);
    const end = toSrtTimestamp(timing.end);
    if (!start || !end) {
      continue;
    }

    cueNumber += 1;
    const cueLines: string[] = [String(cueNumber), `${start} --> ${end}`];
    for (let i = timingIndex + 1; i < lines.length; i += 1) {
      cueLines.push(lines[i]);
    }

    out.push(cueLines.join('\n'));
    out.push('');
  }

  return `${out.join('\n').trimEnd()}\n`;
}

// Count cues by tallying timing lines: works for both SRT and VTT bodies.
export function countCues(text: string): number {
  let count = 0;
  for (const line of text.split('\n')) {
    if (TIMING_LINE.test(line)) {
      count += 1;
    }
  }
  return count;
}
